import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { BASE_URI, prettyPrintDate } from '../Config';
import DownloadButton from './DownloadButton';
import { MhcAttentionJob } from './types';

interface AdminMhcAttentionJobListProps {
    userId: string;
}

const PAGE_SIZE = 10;

const formatModelType = (modelType: string): string => {
    return modelType === "hlae" ? "HLA-E" : "Class I";
};

const formatMultiAlleleStrategy = (strategy?: string): string => {
    if (!strategy) {
        return "Best";
    }
    return strategy === "all" ? "All" : "Best";
};

const AdminMhcAttentionJobList: React.FC<AdminMhcAttentionJobListProps> = ({ userId }) => {
    const { userToken } = useAuth();
    const [jobs, setJobs] = useState<MhcAttentionJob[]>([]);
    const [page, setPage] = useState(0);

    useEffect(() => {
        let ignore = false;

        const fetchJobs = async () => {
            try {
                const response = await fetch(`${BASE_URI}/mhcattention/jobs/list/${userId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userToken}`
                    }
                });
                const data = await response.json();

                if (!ignore && response.ok && Array.isArray(data.jobs)) {
                    setJobs(data.jobs);
                }
            } catch (error) {
                console.error("There was an error during retrieving MHC Attention jobs:", error);
            }
        };

        fetchJobs();
        const intervalId = window.setInterval(fetchJobs, 10000);

        return () => {
            ignore = true;
            window.clearInterval(intervalId);
        };
    }, [userId, userToken]);

    const sortedJobs = [...jobs].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const totalPages = Math.max(1, Math.ceil(sortedJobs.length / PAGE_SIZE));
    const pageSubset = sortedJobs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    useEffect(() => {
        if (page > totalPages - 1) {
            setPage(totalPages - 1);
        }
    }, [page, totalPages]);

    if (jobs.length === 0) {
        return (
            <div className="empty_state">
                <h3>MHC Attention Jobs</h3>
                <p>No MHC Attention jobs have been submitted yet.</p>
            </div>
        );
    }

    return (
        <div>
            <h3 className='jobs'>MHC Attention Jobs</h3>
            <div className="pagination jobs">
                <span className="current_page">Page {page + 1} of {totalPages}</span>
                {page > 0 && <span className='previous_page' onClick={() => setPage(page - 1)}>Previous</span>}
                {page + 1 < totalPages && <span className='next_page' onClick={() => setPage(page + 1)}>Next</span>}
            </div>
            <table>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Input file</th>
                        <th>Model</th>
                        <th>Output Mode</th>
                        <th>Status</th>
                        <th>Input</th>
                        <th>Output</th>
                    </tr>
                </thead>
                <tbody>
                    {pageSubset.map(job => (
                        <tr key={job.id}>
                            <td>{prettyPrintDate(job.created_at)}</td>
                            <td>{job.source_file_name}</td>
                            <td>{formatModelType(job.model_type)}</td>
                            <td>{formatMultiAlleleStrategy(job.multi_allele_strategy)}</td>
                            <td>{job.status}</td>
                            <td>
                                <DownloadButton
                                    text="Link"
                                    filename={`input_${job.source_file_name}`}
                                    url_str={`mhcattention/jobs/${job.id}/download/input`}
                                />
                            </td>
                            <td>
                                {job.status === "Succeeded" && (
                                    <DownloadButton
                                        text="Link"
                                        filename={`output_${job.source_file_name}`}
                                        url_str={`mhcattention/jobs/${job.id}/download/output`}
                                    />
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default AdminMhcAttentionJobList;
